import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import authApi from "services/authService";
import { useStore } from "context/StoreContext";
import { PasswordInput } from "components/ui";
import { getLogoUrl } from "utils/logoUtils";

const ResetPassword = () => {
 const { t } = useTranslation();
 const [searchParams] = useSearchParams();
 const navigate = useNavigate();
 const [password, setPassword] = useState("");
 const [confirmPassword, setConfirmPassword] = useState("");
 const [error, setError] = useState("");
 const [success, setSuccess] = useState(false);
 const [loading, setLoading] = useState(false);
 const [token, setToken] = useState<string | null>(null);
 const { store } = useStore();

 useEffect(() => {
  const tokenParam = searchParams.get("token");
  if (!tokenParam) {
   setError(t("validation.invalid_reset_link"));
  } else {
   setToken(tokenParam);
  }
 }, [searchParams, t]);

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError("");
  setSuccess(false);

  if (password !== confirmPassword) {
   setError(t("validation.passwords_do_not_match"));
   return;
  }

  if (password.length < 8) {
   setError(t("validation.password_must_be_at_least_8_characters_long"));
   return;
  }

  if (!/\d/.test(password)) {
   setError(t("validation.password_must_contain_at_least_one_number"));
   return;
  }

  if (!/[a-zA-Z]/.test(password)) {
   setError(t("validation.password_must_contain_at_least_one_letter"));
   return;
  }

  if (!token) {
   setError(t("validation.invalid_reset_token"));
   return;
  }

  setLoading(true);

  try {
   await authApi.resetPassword(token, password);
   setSuccess(true);
   setTimeout(() => {
    navigate("/login");
   }, 2000);
  } catch (err: any) {
   setError(
    err?.response?.data?.errors?.[0]?.msg ||
     err?.response?.data?.error ||
     err?.response?.data?.message ||
     t("auth.an_error_occurred_please_try_again")
   );
  } finally {
   setLoading(false);
  }
 };

 return (
  <div
   className="flex items-center justify-center min-h-screen"
   style={{
    background: "linear-gradient(135deg, #F5F1ED 0%, #EBE5DE 100%)",
   }}
  >
   <div className="w-full max-w-md">
    {/* Header */}
    <div className="text-center mb-8">
      {store?.logo && (
        <div className="mb-4 flex justify-center">
          <img
            src={getLogoUrl(store.logo)}
            alt={store.name || t("common.appName")}
            className="h-16 w-auto object-contain"
          />
        </div>
      )}
      <h1 className="text-4xl font-bold text-store-primary mb-2 font-store">
        {store?.name || t("common.appName")}
      </h1>
      {!store && (
        <p className="text-store-accent text-sm tracking-widest">
          {t("common.appDescription")}
        </p>
      )}
    </div>

    {/* Form Card */}
    <div className="bg-white  overflow-hidden border-store-accent">
     <form onSubmit={handleSubmit} className="p-8 space-y-6">
      <div>
       <h2 className="text-2xl font-bold text-store-primary mb-1">
        {t("auth.reset_password")}
       </h2>
       <p className="text-store-primary-light text-sm">
        {t("auth.enter_new_password_below")}
       </p>
      </div>

      {error && (
       <div className="bg-red-50 border border-red-200 p-4">
        <p className="text-red-700 text-sm font-medium">{error}</p>
       </div>
      )}

      {success && (
       <div className="bg-green-50 border border-green-200 p-4">
        <p className="text-green-700 text-sm font-medium">
         {t("auth.password_reset_success")}
        </p>
       </div>
      )}

      {!success && token && (
       <>
        <PasswordInput
         label={t("common.labels.newPassword")}
         value={password}
         onChange={(e) => setPassword(e.target.value)}
         placeholder={t("common.placeholders.enterNewPassword")}
         helperText={t("validation.password_min_length", { length: 8 })}
         required
         className="w-full"
        />

        <PasswordInput
         label={t("auth.confirmPassword")}
         value={confirmPassword}
         onChange={(e) => setConfirmPassword(e.target.value)}
         placeholder={t("common.placeholders.confirmNewPassword")}
         required
         className="w-full"
        />

        <button
         type="submit"
         disabled={loading}
         className="w-full bg-store-primary text-white font-semibold py-3 hover:bg-store-secondary transition-all hover: disabled:opacity-50 disabled:cursor-not-allowed"
        >
         {loading ? t("auth.resetting") : t("auth.reset_password")}
        </button>
       </>
      )}

      {!token && (
       <div className="space-y-4">
        <Link
         to="/forgot-password"
         className="block w-full bg-store-primary text-white font-semibold py-3 hover:bg-store-secondary transition-all hover: text-center"
        >
         {t("auth.request_new_reset_link")}
        </Link>
       </div>
      )}
     </form>

     {/* Divider */}
     <div className="px-8 py-4 bg-store-background border-t border-store-accent-light">
      <p className="text-center text-sm text-store-primary-light">
       {t("auth.remember_your_password")}?{" "}
       <Link
        to="/login"
        className="font-semibold text-store-accent hover:text-store-accent-dark transition-colors"
       >
        {t("auth.login_here")}
       </Link>
      </p>
     </div>
    </div>
   </div>
  </div>
 );
};

export default ResetPassword;

